import type { MouseEvent } from "react";
import { memo, useEffect } from "react";

import { noop } from "lodash";

import { Dropdown } from "@similarweb/ui-components";

import { TableDropdownButton } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import type { FlexTableOptions, FlexTableRow } from "app/components/React/Table/FlexTable/types";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";

type Props = {
    tableOptions: FlexTableOptions;
    tableData: readonly FlexTableRow[];
    clickedRowIndex: number;
    setRef<E>(elementName: string, el: E): void;
    onDropdownToggle(isOpen: boolean, isOutsideClick: boolean, e: MouseEvent): void;
    onDropdownClose(rowIndex: number): void;
};

const TableDropdownComponentInner = (props: Props) => {
    const { tableOptions, setRef, tableData, clickedRowIndex, onDropdownToggle, onDropdownClose } = props;

    const { dropdownRef } = useUncontrolledDropdown();

    useEffect(() => {
        setRef("enrichedRowDropdown", dropdownRef.current);
    }, [dropdownRef]);

    if (tableOptions.EnrichedRowComponent) {
        if ((tableOptions.enrichedRowComponentHeight ?? 0) > 0) {
            const {
                enrichedRowComponentAppendTo,
                enrichedRowComponentHeight,
                enrichedRowComponentClass,
                EnrichedRowComponent,
                enrichedRowOptions = {},
                enrichedRowComponentProps = {},
            } = tableOptions;

            const shouldCloseOnClickOut = enrichedRowOptions.isHiddenOnOutsideClick ?? true;

            return (
                <Dropdown
                    ref={dropdownRef}
                    appendTo={enrichedRowComponentAppendTo ?? ".flex-table"}
                    autoPlacement={false}
                    dropdownPopupHeight={enrichedRowComponentHeight}
                    closeOnClickOut={shouldCloseOnClickOut}
                    onToggle={onDropdownToggle}
                    onClick={noop}
                    {...(enrichedRowComponentClass ? { cssClassContainer: enrichedRowComponentClass } : {})}
                >
                    <TableDropdownButton setRef={setRef} />
                    <EnrichedRowComponent
                        row={tableData[clickedRowIndex]}
                        // @ts-ignore FIXME: strict migration - enrichedRowComponentHeight typed as number|undefined but guarded by > 0 check above
                        height={enrichedRowComponentHeight}
                        onHide={() => onDropdownClose(clickedRowIndex)}
                        {...enrichedRowComponentProps}
                    />
                </Dropdown>
            );
        }
    }

    return null;
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
    const { tableOptions, tableData, clickedRowIndex } = prevProps;
    const {
        tableOptions: nextTableOptions,
        tableData: nextTableData,
        clickedRowIndex: nextClickedRowIndex,
    } = nextProps;
    return tableOptions === nextTableOptions && tableData === nextTableData && clickedRowIndex === nextClickedRowIndex;
};
export const TableDropdownComponent = memo(TableDropdownComponentInner, propsAreEqual);
